import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Grid,
  IconButton,
} from "@mui/material";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSelector } from "react-redux";

const WeeklyBedroomCheck = ({ onClose, setOpenDefaultSnackbar, selectedTask }) => {
  const questions = [
    "Are the doors in good working order?",
    "Are the carpets and bathroom floor clean & intact?",
    "Are the floors free from damage?",
    "Does the smoke detector appear physically intact?",
    "Are the lights working?",
    "Are the blinds intact & fully operational?",
    "Are the windows intact and fully operational?",
    "Are the window restrictors intact and fully operational?",
    "Are all furniture items intact and fully operational?",
    "Are all electrical items placed safely?",
    "Are there any visible trailing wires?",
  ];

  const today = new Date();
  const todayDate = today.toISOString().split("T")[0];
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [condition, setCondition] = useState(null);
  const [comments, setComments] = useState("");
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const 
  toggleResponse = (index) => {
    const updatedResponses = [...responses];
    updatedResponses[index] = !updatedResponses[index];
    setResponses(updatedResponses);
  };
  const handleConditionChange = (event, newCondition) => {
    setCondition(newCondition);
  };

  const handleNext = () => {
    setResponses([
      ...responses,
      {
        question: questions[currentQuestionIndex],
        condition,
        comments,
        date: selectedDate,
      },
    ]);
    setCondition(null);
    setComments("");

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const handleSubmit = async () => {
    try {
      // Add `completed_by` to the request payload
      const payload = {
        responses,
        completed_by: user.username, // Include the completed_by field
      };

      await axios.post("/api/bedroomCheck/bedroomCheck", payload);

      if (taskId) {
        updateSubTask(taskId); // Use the stored task ID
      }

      setResponses([]);
      setCurrentQuestionIndex(0);
      setCondition(null);
      setComments("");
      setSelectedDate(todayDate);
      onClose();
    } catch (error) {
      console.error("Error submitting responses:", error);
      alert("Failed to submit responses. Please try again.");
    }
  };

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "#1751D0" }}
        label='Weekly Tasks'
      />

      {/* Header */}
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 1 }}>Weekly Bedroom Check</Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 1 }}>
        Date: {todayDate} &nbsp; • &nbsp; <span style={{ color: "#1751D0" }}>Kindergarten Bedroom</span>
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", fontSize: "14px", mb: 2 }}>
        <Grid container>
          <Grid item xs={5}>
            Questions
          </Grid>
          <Grid item xs={4}>
            <span style={{ color: "#1751D0" }}>{currentQuestionIndex + 1} </span>
            {`/ ${questions.length}`}
          </Grid>
        </Grid>
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={6} md={6}>
          <Grid container spacing={2}>
            <Box
              sx={{
                maxHeight: "500px", // or any height you need
                overflowY: "auto",
                pr: 1, // optional: padding for scrollbar spacing
              }}
            >
              {questions.map((question, index) => (
                <Grid
                  item
                  xs={12}
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <Typography sx={{ flex: 1, color: "black", fontSize: "14px" }}>{question}</Typography>
                  <IconButton
                    color='primary'
                    onClick={() => toggleResponse(index)}
                    sx={{ color: responses[index] + 1 ? "#1751D0" : "#E0E0E0" }}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Grid>
              ))}
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={6} md={6}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={10}>
                  <Typography sx={{ fontSize: "14px", mb: 2, color: "black" }}>
                    {questions[currentQuestionIndex]}
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <ToggleButtonGroup value={condition} exclusive onChange={handleConditionChange}>
                    <ToggleButton value='Yes' sx={{ textTransform: "none" }} color='primary'>
                      Yes
                    </ToggleButton>
                    <ToggleButton value='No' sx={{ textTransform: "none" }} color='error'>
                      No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "14px", mb: 2, color: "black" }}>Comments</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          padding: 2,
          zIndex: 10,
        }}
      >
        {/* Cancel Button */}
        {currentQuestionIndex === 0 && (
          <Button variant='outlined' color='primary' onClick={() => onClose()}>
            Cancel
          </Button>
        )}
        {/* Back Button */}
        {currentQuestionIndex > 0 && (
          <Button variant='outlined' color='primary' onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}>
            Previous
          </Button>
        )}
        {/* Next/Submit Button */}
        {currentQuestionIndex < questions.length - 1 ? (
          <Button variant='contained' color='primary' onClick={handleNext} disabled={!condition}>
            Next
          </Button>
        ) : (
          <Button variant='contained' color='primary' onClick={handleSubmit} disabled={!condition}>
            Submit
          </Button>
        )}{" "}
      </Box>
    </Box>
  );
};

export default WeeklyBedroomCheck;
